import ApiService, { ApiError } from './api.service';

const MIN_SLEEP = 2000;
const MAX_SLEEP = 5000;

export const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export default class BackgroundService {
  private static instance: BackgroundService;

  public static getInstance(): BackgroundService {
    if (!BackgroundService.instance) {
      BackgroundService.instance = new BackgroundService();
    }
    return BackgroundService.instance;
  }

  constructor() {}

  public async waitForJob(
    jobId: string,
    fastest_result: boolean = false,
    onProgress?: (progress: any) => void,
    ignore_404: boolean = false
  ) {
    let jobStatus = 'queued';
    let resp: any;
    let iteration = 1;
    let consecutive404s = 0;
    const max404Retries = 5; // Retry up to 5 times on 404 before giving up

    while (['queued', 'started', 'deferred', 'scheduled'].includes(jobStatus)) {
      if (iteration > 120) {
        throw new Error('Max wait for job exceeded');
      }

      let sleepFor = fastest_result ? 1000 : iteration * 1000;
      if (sleepFor < MIN_SLEEP) {
        sleepFor = MIN_SLEEP;
      }
      if (sleepFor > MAX_SLEEP) {
        sleepFor = MAX_SLEEP;
      }

      await sleep(sleepFor);
      try {
        resp = await ApiService.getInstance().request('GET', `/get-job-status/${jobId}`);
        jobStatus = resp.job_status;
        consecutive404s = 0; // Reset 404 counter on success

        // Report progress if callback provided and progress data exists
        if (onProgress && resp.progress) {
          onProgress({
            status: jobStatus,
            progress: resp.progress,
            iteration,
            // Include any other useful info from resp.meta
            meta: resp.meta,
          });
        }
      } catch (err) {
        // Handle 404 errors
        if (err instanceof ApiError && err.code === 404) {
          consecutive404s += 1;
          
          // If ignore_404 is true, return null immediately (old behavior for old jobs)
          if (ignore_404) {
            return null;
          }
          
          // If we've hit too many consecutive 404s, give up
          if (consecutive404s > max404Retries) {
            throw new Error(`Job status not found after ${max404Retries} attempts. The job may not have been created or may have been cleaned up.`);
          }
          
          // Otherwise, continue polling (job might not be in queue yet)
          // Don't increment iteration here since we want to retry quickly
          continue;
        }
        
        // Re-throw any other errors
        throw err;
      }
      iteration += 1;
    }

    if (resp.job_status === 'failed') {
      throw new Error(resp.error_message || 'Job failed');
    }

    return resp.job_result;
  }
}

